import { useState, useEffect, useMemo } from "react";
import { Box, DialogActions, DialogContent, IconButton, Button, Typography, Grid } from "@mui/material";
import { useFiles } from "../../../../hook/useFiles";
import TextInputComponent from "../../../atom/Inputs/TextInput";
import TextButtonComponet from "../../../atom/Buttons/TextButton";
import WeekDropdown from "../../../atom/Inputs/WeekDropdown";
import { getText } from "../../../../core/Constant";
import { Actions } from "../../../../core/modules/Actions";
import { useSelector, useDispatch } from 'react-redux';
import HeadingComponent from "../../../atom/Headings/Heading";
import TableComponent from "../../../atom/Table/TableComponent";
import DocumentIcon from "../../../atom/Icons/DocumentIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import SelectDocumentModal from "./SelectDocumentModal";

const MAX_TITLE_LENGTH = 2048;

const AdminCourseWeekDocument = ({
  open,
  onClose,
  courseID,
  sectionName,
}) => {
  const dispatch = useDispatch();
  const documentList = useSelector((state) => state.course.get("courseDocumentList"));
  const [selectedDocId, setSelectedDocId] = useState("");
  const [selectedDocName, setSelectedDocName] = useState("");
  const [selectedDocFolderStack, setSelectedDocFolderStack] = useState([]);
  const [openSelectModal, setOpenSelectModal] = useState(false);
  const [selectedDocError, setSelectedDocError] = useState(false);
  const [day, setDay] = useState("");
  const [dayError, setDayError] = useState(false);
  const [isDayValid, setDayValid] = useState(false);
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [titleErrorMsg, setTitleErrorMsg] = useState("Please add the title");
  const [isTitleValid, setTitleValid] = useState(false);
  const [newWeekContent, setNewWeekContent] = useState([]);
  const [saveBtnValidMsg, setSaveBtnValidMsg] = useState("");



  useEffect(() => {
    setSaveBtnValidMsg("");
  }, [courseID, sectionName]);

  useEffect(() => {
    if (!open) {
      setSelectedDocId("");
      setSelectedDocName("");
      setSelectedDocFolderStack([]);
      setOpenSelectModal(false);
      setDay("");
      setTitle("");
      setSelectedDocError(false);
      setDayError(false);
      setDayValid(false);
      setTitleError(false);
      setTitleErrorMsg("Please add the title");
      setTitleValid(false);
      setNewWeekContent([]);
      setSaveBtnValidMsg("");
    }
  }, [open]);

  const filesOnly = useFiles(documentList);

  const removeFromList = (index) => {
    const updatedList = newWeekContent.filter((_, i) => i !== index);
    setNewWeekContent(updatedList);
  };

  const columns = useMemo(() => [
    {
      name: "day",
      label: "Day",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => `Day ${value}`,
      },
    },
    {
      name: "content",
      label: "Title",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => value,
      },
    },
    {
      name: "link",
      label: "Document Name",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const item = newWeekContent[rowIndex];
          const doc = filesOnly.find((d) => String(d.id) === String(value));
          const docName = doc ? doc.name : value;
          const displayString = docName;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <DocumentIcon fileName={docName} />
              <span>{displayString}</span>
            </Box>
          );
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          return (
            <IconButton onClick={() => removeFromList(rowIndex)}>
              <DeleteIcon style={{ color: "#9E9E9E" }} />
            </IconButton>
          );
        },
      },
    },
  ], [filesOnly, newWeekContent]);

  const validateTitle = (text) => {
    const trimmed = text ? text.trim() : "";
    if (!trimmed) {
      setTitleError(true);
      setTitleErrorMsg("Please add the title");
      setTitleValid(false);
      return false;
    }
    if (text.length > MAX_TITLE_LENGTH) {
      setTitleError(true);
      setTitleErrorMsg(`Maximum limit is ${MAX_TITLE_LENGTH} characters.`);
      setTitleValid(false);
      return false;
    }
    setTitleError(false);
    setTitleErrorMsg("Please add the title");
    setTitleValid(true);
    return true;
  };

  const onTitleChange = (e) => {
    const text = getText(e);
    setTitle(text);
    validateTitle(text);
  };

  const onDayChange = (event) => {
    const val = getText(event);
    const isValid = val !== undefined && val !== "";
    setDay(val);
    setDayError(!isValid);
    setDayValid(isValid);
  };

  const addToList = () => {
    setSaveBtnValidMsg("");
    let hasError = false;
    if (!selectedDocId) {
      setSelectedDocError(true);
      hasError = true;
    }
    if (!isDayValid) {
      setDayError(true);
      hasError = true;
    }
    const isTitleOk = validateTitle(title);
    if (!isTitleOk) {
      hasError = true;
    }
    if (hasError) return;

    setNewWeekContent([
      ...newWeekContent,
      {
        content_type: "3", // 3 for Document
        content: title,
        link: String(selectedDocId),
        duration: null,
        day: String(day),
        is_locked: "1",
      }
    ]);

    setSelectedDocId("");
    setSelectedDocName("");
    setSelectedDocFolderStack([]);
    setDay("");
    setTitle("");
    setSelectedDocError(false);
    setDayError(false);
    setDayValid(false);
    setTitleError(false);
    setTitleErrorMsg("Please add the title");
    setTitleValid(false);
  };

  const saveAddedWeekContent = async () => {
    if (newWeekContent.length > 0) {
      const payload = {
        course_id: String(courseID),
        weeks: [
          {
            week: sectionName,
            details: newWeekContent,
          }
        ]
      };

      try {
        await dispatch(Actions.course.createCourseContent(payload));
        onClose();
      } catch (err) {
        console.error("Failed to bind documents:", err);
      }
    } else {
      setSaveBtnValidMsg("Please add at least one document");
    }
  };

  const handleDocumentSelect = (doc, parentId, folderStack) => {
    setSelectedDocId(doc.id);
    setSelectedDocName(doc.name);
    setSelectedDocFolderStack(folderStack);
    const cleanName = doc.name ? doc.name.replace(/\.[^/.]+$/, "") : "";
    setTitle(cleanName);
    validateTitle(cleanName);
    setSelectedDocError(false);
    setOpenSelectModal(false);
  };

  return (
    <>
      <DialogContent>
        <Box className="admin-course-content-form-wrap">
          <Grid container spacing={2} alignItems="flex-start" className="admin-course-input-group">
            {/* Day */}
            <Grid item xs={12} md={2}>
              <WeekDropdown
                selectedValue={day}
                onchange={onDayChange}
                isError={dayError}
                error="Please select a day"
                readOnly={false}
              />
            </Grid>

            {/* Document Select */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  padding: "10px"
                }}
              >
                <p
                  style={{
                    padding: 0,
                    margin: 0,
                    marginBottom: 10,
                    color: "#4E657C",
                    fontSize: 19,
                    fontWeight: 700,
                  }}
                >
                  Document
                </p>
                <Box
                  onClick={() => setOpenSelectModal(true)}
                  className="form-control"
                  sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderRadius: "8px",
                      border: selectedDocError ? "1.5px solid #d32f2f" : "1.5px solid #CCCCCC",
                      cursor: "pointer",
                      backgroundColor: "#FFFFFF",
                    }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, overflow: "hidden" }}>
                    {selectedDocId ? (
                      <>
                        <DocumentIcon fileName={selectedDocName} />
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#495057",
                            fontWeight: 400,
                            fontFamily: "Montserrat",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            direction: "rtl",
                            textAlign: "left",
                            mr: 1
                          }}
                        >
                          {selectedDocName}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#8C8C8C",
                          fontFamily: "Montserrat",
                        }}
                      >
                        Select Document
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="text"
                    sx={{
                      textTransform: "none",
                      color: "#9834F0",
                      fontWeight: 600,
                      fontFamily: "Montserrat",
                      padding: 0,
                      minWidth: "auto",
                      fontSize: "14px",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    {selectedDocId ? "Change" : "Browse"}
                  </Button>
                </Box>
                {selectedDocError && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      fontFamily: "Montserrat",
                      marginLeft: "14px",
                      marginTop: "4px",
                    }}
                  >
                    Please select a document
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Title */}
            <Grid item xs={12} md={4}>
              <TextInputComponent
                label="Title"
                isError={titleError}
                placeholder="Enter Title"
                name="title"
                value={title}
                error={titleErrorMsg}
                maxLength={3000}
                onchange={onTitleChange}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Box sx={{ mt: 5.5 }}>
                <TextButtonComponet text="Add" width={60} onButtonClick={() => addToList()} />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {newWeekContent.length > 0 ? (
          <Box p={3}>
            <Box mt={4} justifyContent="center" alignItems="center">
              <HeadingComponent text="Added Documents" fontweigth={600} size={25} fontfamily="Montserrat" />
            </Box>
            <Box mt={4}>
              <TableComponent
                columns={columns}
                data={newWeekContent}
                filter={false}
                search={false}
                download={false}
                selectableRowsHideCheckboxes={true}
                elevation={0}
                pagination={false}
              />
            </Box>
          </Box>
        ) : null}

        <Box>
          <b>
            <p className="text-center" style={{ color: "red" }}>
              {saveBtnValidMsg}
            </p>
          </b>
        </Box>
      </DialogContent>
      <DialogActions sx={{ mr: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextButtonComponet
            text="Cancel"
            classStyle="btn btn-secondary"
            width={120}
            onButtonClick={() => onClose()}
          />
          <TextButtonComponet
            text="Save"
            width={120}
            onButtonClick={() => saveAddedWeekContent()}
          />
        </Box>
      </DialogActions>
      <SelectDocumentModal
        open={openSelectModal}
        onClose={() => setOpenSelectModal(false)}
        courseID={courseID}
        onSelect={handleDocumentSelect}
        initialFolderStack={selectedDocFolderStack}
        selectedDocId={selectedDocId}
      />
    </>
  );
};

export default AdminCourseWeekDocument;
